import assert from 'assert';

/** Minimum shape every layer type must satisfy. */
export interface LayerBase {
  layerId: string;
  name: string;
}

export interface LayerContextBase<T extends LayerBase> {
  layers: Map<string, T>;
  activeLayerId: string | null;
}

export interface LayerMachineInputBase<T extends LayerBase> {
  initialLayers?: Array<T>;
  /** When `undefined`, defaults to the first layer's id. Pass `null` to start with no selection. */
  activeLayerId?: string | null;
}

export function buildInitialLayerContext<T extends LayerBase>(
  input: LayerMachineInputBase<T>,
  maxLayers: number,
  machineName: string
): LayerContextBase<T> {
  if (input.initialLayers == null) {
    return {layers: new Map(), activeLayerId: null};
  }
  assert(
    input.initialLayers.length <= maxLayers,
    `Cannot initialize ${machineName} with ${input.initialLayers.length} layers. ` +
      `Maximum allowed is ${maxLayers}.`
  );
  const layers = new Map(input.initialLayers.map((layer) => [layer.layerId, layer]));
  let activeLayerId: string | null;
  if (input.activeLayerId !== undefined) {
    assert(
      input.activeLayerId == null || layers.has(input.activeLayerId),
      `activeLayerId "${input.activeLayerId}" not found in initialLayers`
    );
    activeLayerId = input.activeLayerId;
  } else {
    activeLayerId = input.initialLayers[0]?.layerId ?? null;
  }
  return {layers, activeLayerId};
}

export function applyInitLayers<T extends LayerBase>(
  context: LayerContextBase<T>,
  newLayers: Array<T>,
  maxLayers: number,
  machineName: string
): LayerContextBase<T> {
  assert(
    newLayers.length <= maxLayers,
    `Cannot initialize ${machineName} with ${newLayers.length} layers. Maximum allowed is ${maxLayers}.`
  );
  const layers = new Map(newLayers.map((layer) => [layer.layerId, layer]));
  return {
    layers,
    activeLayerId:
      newLayers.find((layer) => layer.layerId === context.activeLayerId) != null
        ? context.activeLayerId
        : null,
  };
}

export function applyAddLayer<T extends LayerBase>(
  context: LayerContextBase<T>,
  newLayer: T,
  maxLayers: number,
  machineName: string
): LayerContextBase<T> {
  assert(
    context.layers.size < maxLayers,
    `Cannot add layer. Maximum allowed is ${maxLayers} for ${machineName}.`
  );
  const layers = new Map(context.layers);
  layers.set(newLayer.layerId, newLayer);
  return {layers, activeLayerId: newLayer.layerId};
}

/** If the removed layer was active, falls back to the first remaining layer. */
export function applyRemoveLayer<T extends LayerBase>(
  context: LayerContextBase<T>,
  layerId: string
): LayerContextBase<T> {
  if (!context.layers.has(layerId)) {
    return context;
  }
  const layers = new Map(context.layers);
  layers.delete(layerId);
  const activeLayerId =
    context.activeLayerId === layerId ? (Array.from(layers.keys())[0] ?? null) : context.activeLayerId;
  return {layers, activeLayerId};
}

export function applySetActiveLayer<T extends LayerBase>(
  context: LayerContextBase<T>,
  layerId: string | null
): LayerContextBase<T> {
  if (layerId != null && !context.layers.has(layerId)) {
    return context;
  }
  return {...context, activeLayerId: layerId};
}

export function applyUpdateLayerName<T extends LayerBase>(
  context: LayerContextBase<T>,
  layerId: string,
  name: string
): LayerContextBase<T> {
  const existing = context.layers.get(layerId);
  if (existing == null) {
    return context;
  }
  const layers = new Map(context.layers);
  layers.set(layerId, {...existing, name});
  return {...context, layers};
}
